import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/contexts/LocaleContext';
import { Gift, Copy, Users, Crown, CheckCircle2, Share2, Loader2, Sparkles, Star, Zap, Award } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = 'https://texhub.com.mx';

const ReferralWidget = () => {
  const { user } = useAuth();
  const { isUSA } = useLocale();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchReferralStats();
  }, []);

  const fetchReferralStats = async () => {
    try {
      const token = localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token');
      const res = await fetch(`${API_URL}/api/referral/stats/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching referral stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const referralLink = stats?.referral_code 
    ? `${SITE_URL}/ref/${stats.referral_code}`
    : '';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success(isUSA ? 'Link copied!' : '¡Link copiado!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = referralLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      toast.success(isUSA ? 'Link copied!' : '¡Link copiado!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'TexHub - Marketplace Textil',
          text: isUSA 
            ? 'Join TexHub, the textile marketplace!' 
            : '¡Únete a TexHub, el marketplace textil!',
          url: referralLink
        });
      } catch (err) {
        copyLink();
      }
    } else {
      copyLink();
    }
  };

  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
        </CardContent>
      </Card>
    );
  }

  if (!stats) return null;

  // Calculate progress percentages
  const progressToFeatured = Math.min(100, (stats.referrals_count / 3) * 100);
  const progressToPro = Math.min(100, (stats.referrals_count / 5) * 100);
  const progressToBadge = Math.min(100, (stats.referrals_count / 10) * 100);

  // Get next reward info
  const getNextReward = () => {
    const count = stats.referrals_count;
    if (count < 1) return { credits: 5, label: isUSA ? '1st referral' : '1er referido' };
    if (count < 2) return { credits: 5, label: isUSA ? '2nd referral' : '2do referido' };
    if (count < 3) return { credits: 7, label: isUSA ? '3rd referral' : '3er referido' };
    if (count < 5) return { credits: 7, label: isUSA ? 'Next referral' : 'Próximo referido' };
    if (count < 10) return { credits: 10, label: isUSA ? 'Next referral' : 'Próximo referido' };
    return { credits: 10, label: isUSA ? 'Each referral' : 'Cada referido' };
  };

  const nextReward = getNextReward();

  return (
    <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Gift className="w-5 h-5 text-emerald-600" />
          {isUSA ? 'Invite & Earn' : 'Invita y Gana'}
          {stats.referral_badge === 'embajador' && (
            <Badge className="bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs ml-2">
              <Award className="w-3 h-3 mr-1" />
              Embajador
            </Badge>
          )}
        </CardTitle>
        <p className="text-sm text-slate-500 mt-1">
          {isUSA 
            ? 'Share your link. When they complete their profile, you earn credits!' 
            : '¡Comparte tu link. Cuando completen su perfil, ganas créditos!'}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Credits Summary */}
        {stats.credits && (
          <div className="bg-white/80 rounded-lg p-3 border border-emerald-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700 flex items-center gap-1">
                <Zap className="w-4 h-4 text-amber-500" />
                {isUSA ? 'Credits earned' : 'Créditos ganados'}
              </span>
              <Badge className="bg-amber-100 text-amber-700 font-bold">
                {stats.credits.total_earned} {isUSA ? 'total' : 'total'}
              </Badge>
            </div>
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>{isUSA ? 'This month:' : 'Este mes:'}</span>
              <span>{stats.credits.monthly_earned}/{stats.credits.monthly_limit} máx</span>
            </div>
            <Progress 
              value={(stats.credits.monthly_earned / stats.credits.monthly_limit) * 100} 
              className="h-1.5 mt-1" 
            />
          </div>
        )}

        {/* Next Reward Highlight */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-3 border border-purple-200">
          <p className="text-xs text-purple-600 font-medium mb-1">
            {isUSA ? 'Next reward:' : 'Próxima recompensa:'}
          </p>
          <div className="flex items-center justify-between">
            <span className="text-lg font-bold text-purple-700">
              +{nextReward.credits} {isUSA ? 'credits' : 'créditos'}
            </span>
            <span className="text-xs text-purple-500">{nextReward.label}</span>
          </div>
        </div>

        {/* Referral Link */}
        <div>
          <p className="text-sm text-slate-600 mb-2 font-medium">
            {isUSA ? 'Your referral link:' : 'Tu link de invitación:'}
          </p>
          <div className="flex gap-2">
            <div className="flex-1 bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 truncate font-mono">
              {referralLink}
            </div>
            <Button 
              size="sm" 
              variant="outline"
              onClick={copyLink}
              className={copied ? 'bg-emerald-50 border-emerald-300' : ''}
            >
              {copied ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </Button>
            <Button 
              size="sm" 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={shareLink}
            >
              <Share2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between py-3 border-t border-emerald-100">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-500" />
            <span className="text-sm text-slate-600">
              {isUSA ? 'Friends invited:' : 'Amigos invitados:'}
            </span>
          </div>
          <Badge variant="outline" className="bg-white font-bold">
            {stats.referrals_count}
          </Badge>
        </div>

        {/* Rewards Section - Milestones */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-3 border border-amber-200">
          <p className="text-xs font-semibold text-amber-800 mb-3 flex items-center gap-1">
            <Star className="w-3 h-3 text-amber-500" />
            {isUSA ? 'Milestone rewards:' : 'Recompensas por hitos:'}
          </p>
          
          <div className="space-y-3">
            {/* Featured Badge - 3 referrals */}
            <div>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-slate-700 flex items-center gap-1.5">
                  <Crown className="w-4 h-4 text-amber-500" />
                  <span className="font-medium">{isUSA ? 'Featured Profile' : 'Perfil Destacado'}</span>
                </span>
                <span className="text-xs text-slate-500 font-medium">
                  {Math.min(stats.referrals_count, 3)}/3
                </span>
              </div>
              <Progress value={progressToFeatured} className="h-2" />
              {stats.is_featured && (
                <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1 font-medium">
                  <CheckCircle2 className="w-3 h-3" />
                  {isUSA ? 'Unlocked!' : '¡Desbloqueado!'}
                </p>
              )}
            </div>

            {/* Pro Days - 5 referrals */}
            <div>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-slate-700 flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4 text-purple-500" />
                  <span className="font-medium">{isUSA ? '+30 days PRO' : '+30 días PRO'}</span>
                </span>
                <span className="text-xs text-slate-500 font-medium">
                  {Math.min(stats.referrals_count, 5)}/5
                </span>
              </div>
              <Progress value={progressToPro} className="h-2" />
              {stats.referrals_count >= 5 && (
                <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1 font-medium">
                  <CheckCircle2 className="w-3 h-3" />
                  {isUSA ? 'Earned!' : '¡Ganado!'}
                </p>
              )}
            </div>

            {/* Ambassador Badge - 10 referrals */}
            <div>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-slate-700 flex items-center gap-1.5">
                  <Award className="w-4 h-4 text-orange-500" />
                  <span className="font-medium">{isUSA ? 'Ambassador + 50 credits' : 'Embajador + 50 créditos'}</span>
                </span>
                <span className="text-xs text-slate-500 font-medium">
                  {Math.min(stats.referrals_count, 10)}/10
                </span>
              </div>
              <Progress value={progressToBadge} className="h-2" />
              {stats.referral_badge === 'embajador' && (
                <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1 font-medium">
                  <CheckCircle2 className="w-3 h-3" />
                  {isUSA ? 'Badge earned!' : '¡Badge ganado!'}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Credit Scale Info */}
        <div className="text-xs text-slate-500 bg-white/50 rounded-lg p-2">
          <p className="font-medium mb-1">{isUSA ? 'Credits per referral:' : 'Créditos por referido:'}</p>
          <p>1-2: 5 | 3-4: 7 | 5-9: 10 | 10+: 50 bonus</p>
          <p className="mt-1 text-slate-400">{isUSA ? 'Max 50 credits/month' : 'Máx 50 créditos/mes'}</p>
        </div>

        {/* Recent Referrals */}
        {stats.referrals?.length > 0 && (
          <div className="pt-3 border-t border-emerald-100">
            <p className="text-xs text-slate-500 mb-2 font-medium">
              {isUSA ? 'Recent friends:' : 'Amigos recientes:'}
            </p>
            <div className="space-y-1">
              {stats.referrals.slice(0, 3).map((ref, i) => (
                <div key={i} className="flex items-center justify-between text-sm bg-white/50 rounded px-2 py-1">
                  <span className="text-slate-700">{ref.name}</span>
                  <div className="flex items-center gap-2">
                    {ref.onboarding_completed && (
                      <CheckCircle2 className="w-3 h-3 text-emerald-500" title={isUSA ? 'Completed profile' : 'Perfil completo'} />
                    )}
                    <span className="text-xs text-slate-400">
                      {new Date(ref.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ReferralWidget;
